import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import NextImage from 'next/image'
import React from 'react'

import type { Props as MediaProps } from '../types'

import { cssVariables } from '@/cssVariables'
import { getMediaUrl } from '@/utilities/getMediaUrl'

const { breakpoints } = cssVariables

// "The Loading Ground" — branded placeholder shown while any CMS image streams in
// (design spec artifact 2026-08-17): theme/card ground, cyan bloom top-right,
// green bloom bottom-left. Replaces the template's murky grey-noise PNG.
const placeholderBlur =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid slice'%3E%3Cdefs%3E%3ClinearGradient id='b' x1='0' y1='0' x2='0' y2='1'%3E%3Cstop offset='0' stop-color='%23eceaec'/%3E%3Cstop offset='.65' stop-color='%23e0dfe0'/%3E%3C/linearGradient%3E%3CradialGradient id='c' cx='86%25' cy='8%25' r='90%25'%3E%3Cstop offset='0' stop-color='%2374cdde' stop-opacity='.3'/%3E%3Cstop offset='.58' stop-color='%2374cdde' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='g' cx='8%25' cy='96%25' r='100%25'%3E%3Cstop offset='0' stop-color='%238cc640' stop-opacity='.24'/%3E%3Cstop offset='.58' stop-color='%238cc640' stop-opacity='0'/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23b)'/%3E%3Crect width='100' height='100' fill='url(%23c)'/%3E%3Crect width='100' height='100' fill='url(%23g)'/%3E%3C/svg%3E"

/**
 * ImageMedia
 *
 * This component passes a **relative** `src` (e.g. `/media/...`) to Next.js Image.
 * The `getMediaUrl` utility constructs the full URL by prepending the base URL from env vars
 * (NEXT_PUBLIC_SERVER_URL). Next.js then optimizes this using `remotePatterns` configured
 * in next.config.js — no custom `loader` needed.
 *
 * Flow:
 *   1. Resource URL from Payload: `/media/image-123.jpg`
 *   2. getMediaUrl() adds base URL: `https://yourdomain.com/media/image-123.jpg`
 *   3. Next.js Image optimizes via remotePatterns: `/_next/image?url=...&w=1200&q=75`
 *
 * If your storage/plugin returns **external CDN URLs** (e.g. `https://cdn.example.com/...`),
 * choose ONE of the following:
 *   A) Allow the remote host in next.config.js:
 *      images: { remotePatterns: [{ protocol: 'https', hostname: 'cdn.example.com' }] }
 *   B) Provide a **custom loader** for CDN-specific transforms:
 *      const imageLoader: ImageLoader = ({ src, width, quality }) =>
 *        `https://cdn.example.com${src}?w=${width}&q=${quality ?? 75}`
 *      <Image loader={imageLoader} src="/media/hero.jpg" width={1200} height={600} alt="" />
 *   C) Skip optimization:
 *      <Image unoptimized src="https://cdn.example.com/hero.jpg" width={1200} height={600} alt="" />
 *
 * TL;DR: Template uses relative URLs + getMediaUrl() to construct full URLs, then relies on
 * remotePatterns for optimization. Only add `loader` if using external CDNs with custom transforms.
 */

export const ImageMedia: React.FC<MediaProps> = (props) => {
  const {
    alt: altFromProps,
    fill,
    pictureClassName,
    imgClassName,
    priority,
    resource,
    size: sizeFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
  } = props

  let width: number | undefined
  let height: number | undefined
  let alt = altFromProps
  let src: StaticImageData | string = srcFromProps || ''
  let focalX: number | undefined
  let focalY: number | undefined

  if (!src && resource && typeof resource === 'object') {
    const { alt: altFromResource, height: fullHeight, url, width: fullWidth } = resource

    width = fullWidth!
    height = fullHeight!
    alt = altFromResource || ''
    focalX = resource.focalX ?? undefined
    focalY = resource.focalY ?? undefined

    const cacheTag = resource.updatedAt

    src = getMediaUrl(url, cacheTag)
  }

  const loading = loadingFromProps || (!priority ? 'lazy' : undefined)

  // NOTE: this is used by the browser to determine which image to download at
  // different screen sizes. Default to viewport width — the old breakpoint-map
  // string made phones request the 3840px bucket (1.7MB images on mobile,
  // PageSpeed audit 2026-08-18). Blocks with known column widths pass `size`.
  const sizes = sizeFromProps ?? '100vw'

  return (
    <picture className={cn(pictureClassName)}>
      <NextImage
        alt={alt || ''}
        className={cn(imgClassName)}
        fill={fill}
        height={!fill ? height : undefined}
        placeholder="blur"
        blurDataURL={placeholderBlur}
        priority={priority}
        quality={80}
        loading={loading}
        sizes={sizes}
        src={src}
        width={!fill ? width : undefined}
        style={
          focalX != null && focalY != null ? { objectPosition: `${focalX}% ${focalY}%` } : undefined
        }
      />
    </picture>
  )
}
